/**
 * HRPanels.jsx — extended HR tabs: Dashboard, Shifts, Transfers, Overtime,
 * Production Lines, Uniforms, Loans, Documents, Reports.
 *
 * Kept as a companion file so HRPage.jsx stays readable.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { inr, fmtDate } from "@/lib/utils-format";
import {
  Users, UserPlus, UserMinus, ArrowRightLeft, Clock, Factory, Timer, Shirt,
  BadgeIndianRupee, FileWarning, CalendarDays, TrendingUp, AlertTriangle, CheckCircle2,
  Download, Plus, Trash2, IndianRupee, ClipboardList,
} from "lucide-react";

const cls = (a, b) => `${a} ${b || ""}`.trim();

/* ============================================================ */
/* DASHBOARD                                                    */
/* ============================================================ */
export function HRDashboardPanel({ shifts = [], branches = [] }) {
  const [d, setD] = useState(null);
  const [docs, setDocs] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const morningShift = useMemo(() => shifts.find((s) => (s.kind || "").toLowerCase() === "morning") || shifts[0] || null, [shifts]);
  const [bulkForm, setBulkForm] = useState({ shift_id: "", branch_id: "", status: "present" });
  useEffect(() => { if (morningShift && !bulkForm.shift_id) setBulkForm((p) => ({ ...p, shift_id: morningShift.id })); }, [morningShift]);
  const reload = () => {
    api.get("/hr/dashboard").then((r) => setD(r.data)).catch(() => {});
    api.get("/hr/documents").then((r) => setDocs(r.data)).catch(() => {});
  };
  useEffect(() => { reload(); }, []);
  const upcomingDocs = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const in30 = new Date(Date.now() + 30 * 86400e3).toISOString().slice(0, 10);
    return docs.filter((x) => x.expiry_date && x.expiry_date >= today && x.expiry_date <= in30).slice(0, 8);
  }, [docs]);
  if (!d) return <div className="p-6 text-[#8B7355]">Loading dashboard…</div>;
  const t = d.totals; const p = d.payroll;

  const bulkMark = async () => {
    if (!bulkForm.shift_id) return toast.error("Pick a shift");
    try {
      const r = await api.post("/hr/attendance/bulk-mark", {
        date: new Date().toISOString().slice(0, 10),
        shift_id: bulkForm.shift_id,
        branch_id: bulkForm.branch_id || null,
        status: bulkForm.status,
        only_missing: true,
      });
      toast.success(`Marked ${r.data.marked} employee(s) ${bulkForm.status} for ${r.data.shift_name || "shift"}`);
      setBulkOpen(false);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk-mark failed"); }
  };

  return (
    <div className="pt-4 space-y-4" data-testid="hr-dashboard-panel">
      {/* Quick actions strip */}
      <div className="flex flex-wrap items-center justify-between gap-2 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-orange-600 text-white flex items-center justify-center"><CalendarDays className="w-5 h-5" /></div>
          <div>
            <div className="text-sm font-[Outfit] font-semibold text-[#2C1810]">Quick Attendance</div>
            <div className="text-xs text-[#8B7355]">{t.unmarked_today > 0 ? `${t.unmarked_today} employees not yet marked today` : "All employees marked for today ✓"}</div>
          </div>
        </div>
        <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
          <DialogTrigger asChild>
            <Button data-testid="hr-bulk-mark-btn" className="bg-orange-600 hover:bg-orange-700 text-white" disabled={t.unmarked_today === 0}>
              <CheckCircle2 className="w-4 h-4 mr-1" /> Mark all present today
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader><DialogTitle className="font-[Outfit]">Bulk Attendance — {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "short" })}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Shift *</Label>
                <Select value={bulkForm.shift_id} onValueChange={(v) => setBulkForm({ ...bulkForm, shift_id: v })}>
                  <SelectTrigger data-testid="hr-bulk-shift"><SelectValue placeholder="Select shift" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-72">
                    {shifts.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.start_time}–{s.end_time})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Branch (optional — leave blank to include all)</Label>
                <Select value={bulkForm.branch_id || "__none__"} onValueChange={(v) => setBulkForm({ ...bulkForm, branch_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="hr-bulk-branch"><SelectValue placeholder="All branches" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-72">
                    <SelectItem value="__none__">All branches</SelectItem>
                    {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name} · {b.type}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={bulkForm.status} onValueChange={(v) => setBulkForm({ ...bulkForm, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="present">Present</SelectItem>
                    <SelectItem value="absent">Absent</SelectItem>
                    <SelectItem value="half_day">Half-day</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="text-xs text-[#8B7355] bg-[#FAF6EE] rounded p-2 border border-[#EAE5D9]">
                Only employees who <b>don't yet have a punch</b> for today at the selected shift will be created. You can override individual employees afterwards from the Attendance tab.
              </div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button><Button data-testid="hr-bulk-mark-save" onClick={bulkMark} className="bg-[#C05621] text-white">Mark {t.unmarked_today}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <KPI label="Total Employees" value={t.total_employees} icon={Users} tone="slate" />
        <KPI label="Present Today" value={t.present_today} icon={CheckCircle2} tone="green" />
        <KPI label="Absent Today" value={t.absent_today} icon={UserMinus} tone="red" />
        <KPI label="On Leave" value={t.on_leave_today} icon={CalendarDays} tone="amber" />
        <KPI label="Late Today" value={t.late_today} icon={Clock} tone="amber" />
        <KPI label="New Joiners (30d)" value={t.new_joiners_30d} icon={UserPlus} tone="blue" />
        <KPI label="Docs Expiring (30d)" value={t.doc_expiry_upcoming} icon={FileWarning} tone={t.doc_expiry_upcoming > 0 ? "red" : "slate"} />
        <KPI label="Unmarked Today" value={t.unmarked_today} icon={AlertTriangle} tone={t.unmarked_today > 0 ? "amber" : "slate"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-[#EAE5D9] rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-[Outfit] text-lg font-semibold flex items-center gap-2">
              <BadgeIndianRupee className="w-5 h-5 text-purple-700" /> Payroll · {p.month}
            </h3>
            <Badge className="bg-purple-100 text-purple-800 border-0">{p.records} records</Badge>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <StatBig label="Total" value={inr(p.total)} tone="slate" />
            <StatBig label="Paid" value={inr(p.paid)} tone="green" />
            <StatBig label="Unpaid" value={inr(p.unpaid)} tone="red" />
          </div>
          <div className="mt-3 h-2 rounded-full bg-stone-100 overflow-hidden">
            <div className="h-full bg-emerald-500" style={{ width: `${p.total ? (p.paid / p.total) * 100 : 0}%` }} />
          </div>
        </div>
        <div className="bg-white border border-[#EAE5D9] rounded-lg p-4">
          <h3 className="font-[Outfit] text-lg font-semibold flex items-center gap-2 mb-3">
            <FileWarning className="w-5 h-5 text-red-700" /> Documents Expiring Soon
          </h3>
          <div className="space-y-1.5 text-sm" data-testid="hr-doc-expiry-list">
            {upcomingDocs.length === 0 && <div className="text-[#8B7355] italic text-xs">No documents expiring in the next 30 days.</div>}
            {upcomingDocs.map((x) => (
              <div key={x.id} className="flex items-center justify-between border-b border-[#EAE5D9] last:border-0 py-1.5">
                <div>
                  <div className="font-medium text-[#2C1810]">{x.employee_name}</div>
                  <div className="text-xs text-[#8B7355] capitalize">{x.doc_type.replace(/_/g, " ")} · {x.doc_number || "—"}</div>
                </div>
                <Badge className="bg-red-100 text-red-800 border-0">{fmtDate(x.expiry_date)}</Badge>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, icon: Icon, tone }) {
  const toneMap = {
    green: "bg-emerald-50 border-emerald-200 text-emerald-700",
    red: "bg-red-50 border-red-200 text-red-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    purple: "bg-purple-50 border-purple-200 text-purple-700",
    slate: "bg-stone-50 border-stone-200 text-stone-700",
  };
  return (
    <div className={cls("border rounded-md p-3", toneMap[tone] || toneMap.slate)}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider opacity-70">{label}</span>
        {Icon && <Icon className="w-4 h-4 opacity-70" />}
      </div>
      <div className="text-2xl font-[Outfit] font-semibold mt-1">{value ?? 0}</div>
    </div>
  );
}
function StatBig({ label, value, tone }) {
  const c = tone === "green" ? "text-emerald-700" : tone === "red" ? "text-red-700" : "text-[#2C1810]";
  return (
    <div className="border border-[#EAE5D9] rounded-md p-3 bg-[#FAF6EE]">
      <div className="text-[10px] uppercase text-[#8B7355]">{label}</div>
      <div className={cls("text-xl font-[Outfit] font-semibold", c)}>{value}</div>
    </div>
  );
}

/* ============================================================ */
/* SHIFTS                                                       */
/* ============================================================ */
export function ShiftsPanel({ branches }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", kind: "morning", start_time: "09:00", end_time: "18:00", branch_id: "", active: true });
  const load = () => api.get("/hr/shifts").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!f.name || !f.start_time || !f.end_time) return toast.error("Name / times required");
    try {
      await api.post("/hr/shifts", { ...f, branch_id: f.branch_id || null });
      toast.success("Shift saved"); setOpen(false);
      setF({ name: "", kind: "morning", start_time: "09:00", end_time: "18:00", branch_id: "", active: true }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete this shift?")) return; await api.delete(`/hr/shifts/${id}`); load(); };
  return (
    <div className="pt-4 space-y-3" data-testid="hr-shifts-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-[Outfit] text-lg font-semibold">Shift Patterns</h3>
          <p className="text-xs text-[#8B7355]">Configure per-branch or global shifts (Morning / Afternoon / Night / Custom).</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="hr-shift-add" className="bg-[#C05621] text-white"><Plus className="w-4 h-4 mr-1" /> New Shift</Button></DialogTrigger>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader><DialogTitle>New Shift Pattern</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Name *</Label><Input data-testid="hr-shift-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Morning" /></div>
              <div><Label>Kind</Label>
                <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="morning">Morning</SelectItem>
                    <SelectItem value="afternoon">Afternoon</SelectItem>
                    <SelectItem value="night">Night</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Branch</Label>
                <Select value={f.branch_id || "__none__"} onValueChange={(v) => setF({ ...f, branch_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="hr-shift-branch"><SelectValue placeholder="Global (all branches)" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-72">
                    <SelectItem value="__none__">Global (all branches)</SelectItem>
                    {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name} · {b.type}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Start *</Label><Input type="time" data-testid="hr-shift-start" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} /></div>
              <div><Label>End *</Label><Input type="time" data-testid="hr-shift-end" value={f.end_time} onChange={(e) => setF({ ...f, end_time: e.target.value })} /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="hr-shift-save" onClick={save} className="bg-[#2C1810] text-white">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="rounded-lg border border-[#EAE5D9] overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#FAF6EE] text-[#8B7355] text-xs uppercase"><tr><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Kind</th><th className="text-left px-3 py-2">Timing</th><th className="text-left px-3 py-2">Branch</th><th className="text-left px-3 py-2">Status</th><th></th></tr></thead>
          <tbody data-testid="hr-shifts-rows">
            {rows.map((s) => {
              const b = branches.find((x) => x.id === s.branch_id);
              return (
                <tr key={s.id} className="border-t border-[#EAE5D9]">
                  <td className="px-3 py-2 font-medium">{s.name}</td>
                  <td className="px-3 py-2 capitalize"><Badge className="bg-blue-100 text-blue-800 border-0">{s.kind}</Badge></td>
                  <td className="px-3 py-2 font-mono text-xs">{s.start_time} → {s.end_time}</td>
                  <td className="px-3 py-2 text-xs">{b ? `${b.name} (${b.type})` : <span className="text-[#8B7355] italic">Global</span>}</td>
                  <td className="px-3 py-2">{s.active ? <Badge className="bg-emerald-100 text-emerald-800 border-0">active</Badge> : <Badge className="bg-stone-100 text-stone-600 border-0">inactive</Badge>}</td>
                  <td className="px-3 py-2 text-right"><button onClick={() => del(s.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-[#8B7355]">No shifts. Click "+ New Shift" or the defaults will auto-seed on first fetch.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============================================================ */
/* TRANSFERS                                                    */
/* ============================================================ */
export function TransfersPanel({ employees, branches }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ employee_id: "", to_branch_id: "", effective_date: new Date().toISOString().slice(0, 10), reason: "" });
  const load = () => api.get("/hr/transfers").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!f.employee_id || !f.to_branch_id) return toast.error("Employee & destination required");
    try { await api.post("/hr/transfers", f); toast.success("Transfer recorded"); setOpen(false); setF({ employee_id: "", to_branch_id: "", effective_date: new Date().toISOString().slice(0, 10), reason: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";
  return (
    <div className="pt-4 space-y-3" data-testid="hr-transfers-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-[Outfit] text-lg font-semibold">Multi-Branch Employee Transfers</h3>
          <p className="text-xs text-[#8B7355]">Move an employee from one factory / showroom / HO to another. Their primary branch is updated instantly and history preserved.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="hr-transfer-add" className="bg-[#C05621] text-white"><ArrowRightLeft className="w-4 h-4 mr-1" /> New Transfer</Button></DialogTrigger>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader><DialogTitle>Transfer Employee</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Employee *</Label>
                <Select value={f.employee_id} onValueChange={(v) => setF({ ...f, employee_id: v })}>
                  <SelectTrigger data-testid="hr-transfer-emp"><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-72">{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} · {e.employee_code}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>To Branch *</Label>
                <Select value={f.to_branch_id} onValueChange={(v) => setF({ ...f, to_branch_id: v })}>
                  <SelectTrigger data-testid="hr-transfer-to"><SelectValue placeholder="Select destination" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-72">{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name} · {b.type}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Effective Date</Label><Input type="date" value={f.effective_date} onChange={(e) => setF({ ...f, effective_date: e.target.value })} /></div>
                <div><Label>Reason</Label><Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="e.g. Expansion" /></div>
              </div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="hr-transfer-save" className="bg-[#2C1810] text-white">Transfer</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="rounded-lg border border-[#EAE5D9] overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#FAF6EE] text-[#8B7355] text-xs uppercase"><tr><th className="text-left px-3 py-2">Employee</th><th className="text-left px-3 py-2">From</th><th className="text-left px-3 py-2">To</th><th className="text-left px-3 py-2">Effective</th><th className="text-left px-3 py-2">Reason</th></tr></thead>
          <tbody data-testid="hr-transfers-rows">
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#EAE5D9]">
                <td className="px-3 py-2">{r.employee_name} <span className="text-xs text-[#8B7355] font-mono">· {r.employee_code}</span></td>
                <td className="px-3 py-2">{branchName(r.from_branch_id) || <span className="italic text-[#8B7355]">—</span>}</td>
                <td className="px-3 py-2"><ArrowRightLeft className="w-3 h-3 inline mr-1 text-[#C05621]" /> {branchName(r.to_branch_id)}</td>
                <td className="px-3 py-2 text-xs">{fmtDate(r.effective_date)}</td>
                <td className="px-3 py-2 text-xs">{r.reason || "—"}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-[#8B7355]">No transfers yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============================================================ */
/* OVERTIME                                                     */
/* ============================================================ */
export function OvertimePanel({ employees }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ employee_id: "", date: new Date().toISOString().slice(0, 10), hours: 1, rate_multiplier: 1.5, reason: "", manual_amount: "" });
  const [ratePreview, setRatePreview] = useState(null);
  const [deptRatesOpen, setDeptRatesOpen] = useState(false);
  const [deptRates, setDeptRates] = useState({});
  const load = () => api.get("/hr/overtime").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!f.employee_id) { setRatePreview(null); return; }
    api.get("/hr/overtime/rate", { params: { employee_id: f.employee_id } })
      .then((r) => setRatePreview(r.data)).catch(() => setRatePreview(null));
  }, [f.employee_id]);
  const openDeptRates = async () => {
    try { const r = await api.get("/hr/ot-rates"); setDeptRates(r.data.department_rates || {}); setDeptRatesOpen(true); }
    catch { toast.error("Failed to load rates"); }
  };
  const saveDeptRates = async () => {
    try { await api.put("/hr/ot-rates", { rates: deptRates }); toast.success("Department OT rates updated"); setDeptRatesOpen(false); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const save = async () => {
    if (!f.employee_id || !f.date || !f.hours) return toast.error("Employee / date / hours required");
    try {
      const payload = { ...f, hours: Number(f.hours), rate_multiplier: Number(f.rate_multiplier) };
      if (f.manual_amount === "" || f.manual_amount == null) delete payload.manual_amount;
      else payload.manual_amount = Number(f.manual_amount);
      await api.post("/hr/overtime", payload);
      toast.success("Overtime marked");
      setOpen(false); setF({ employee_id: "", date: new Date().toISOString().slice(0, 10), hours: 1, rate_multiplier: 1.5, reason: "", manual_amount: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete this OT entry?")) return; await api.delete(`/hr/overtime/${id}`); load(); };
  const total = rows.reduce((s, r) => s + Number(r.amount || 0), 0);
  const computedAmount = ratePreview && f.hours && f.rate_multiplier
    ? Math.round(ratePreview.hourly_rate * Number(f.hours) * Number(f.rate_multiplier) * 100) / 100
    : 0;
  const rateSourceLabel = {
    employee_override: "Employee override",
    department: "Department default",
    salary_derived: "Salary ÷ 240",
  };
  return (
    <div className="pt-4 space-y-3" data-testid="hr-ot-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-[Outfit] text-lg font-semibold">Overtime — Manager Marks Directly</h3>
          <p className="text-xs text-[#8B7355]">Rate precedence: employee override → department default → salary÷240. Manual amount trumps all.</p>
        </div>
        <div className="flex items-center gap-3">
          <Button size="sm" variant="outline" onClick={openDeptRates} data-testid="hr-ot-dept-rates-btn"><BadgeIndianRupee className="w-4 h-4 mr-1" /> Department Rates</Button>
          <Badge className="bg-purple-100 text-purple-800 border-0">Total OT ₹ {inr(total)}</Badge>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="hr-ot-add" className="bg-[#C05621] text-white"><Timer className="w-4 h-4 mr-1" /> Mark OT</Button></DialogTrigger>
            <DialogContent className="max-w-md bg-white">
              <DialogHeader><DialogTitle>Record Overtime</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Employee *</Label>
                  <Select value={f.employee_id} onValueChange={(v) => setF({ ...f, employee_id: v })}>
                    <SelectTrigger data-testid="hr-ot-emp"><SelectValue placeholder="Select employee" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-72">{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} · {e.employee_code}</SelectItem>)}</SelectContent>
                  </Select>
                </div>

                {ratePreview && (
                  <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm" data-testid="hr-ot-rate-preview">
                    <div className="flex items-center justify-between">
                      <span>Effective hourly rate</span>
                      <span className="font-semibold text-blue-800">₹{inr(ratePreview.hourly_rate)}/h</span>
                    </div>
                    <div className="text-xs text-blue-700 mt-0.5">Source: <b>{rateSourceLabel[ratePreview.source] || ratePreview.source}</b>{ratePreview.department ? ` · dept: ${ratePreview.department}` : ""}</div>
                  </div>
                )}

                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Date</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
                  <div><Label>Hours</Label><Input type="number" step="0.5" value={f.hours} onChange={(e) => setF({ ...f, hours: e.target.value })} data-testid="hr-ot-hours" /></div>
                  <div><Label>Rate ×</Label><Input type="number" step="0.1" value={f.rate_multiplier} onChange={(e) => setF({ ...f, rate_multiplier: e.target.value })} /></div>
                </div>
                <div><Label>Reason</Label><Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="e.g. Weekend production" /></div>
                <div className="border border-[#EAE5D9] rounded p-2 bg-[#FAF6EE]">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-[#8B7355]">Computed amount</span>
                    <span className="font-semibold">₹{inr(computedAmount)}</span>
                  </div>
                  <Label className="text-xs">Manual amount override (₹) — leave blank to use computed</Label>
                  <Input type="number" value={f.manual_amount} onChange={(e) => setF({ ...f, manual_amount: e.target.value })} placeholder="0" data-testid="hr-ot-manual" />
                </div>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="hr-ot-save" className="bg-[#2C1810] text-white">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Dept Rates dialog */}
          <Dialog open={deptRatesOpen} onOpenChange={setDeptRatesOpen}>
            <DialogContent className="max-w-md bg-white">
              <DialogHeader><DialogTitle>Department OT Hourly Rates</DialogTitle></DialogHeader>
              <div className="space-y-2 text-sm">
                <p className="text-xs text-[#8B7355]">Set an hourly OT rate (₹/h) for each department. Employees inherit this unless they have a personal override.</p>
                {["production", "packaging", "sales", "delivery", "kitchen", "quality", "maintenance", "admin", "hr", "accounts"].map((d) => (
                  <div key={d} className="flex items-center justify-between gap-3">
                    <Label className="capitalize flex-1">{d}</Label>
                    <Input type="number" className="w-32" placeholder="—" value={deptRates[d] ?? ""}
                      onChange={(e) => setDeptRates({ ...deptRates, [d]: e.target.value === "" ? "" : Number(e.target.value) })}
                      data-testid={`hr-ot-rate-${d}`} />
                  </div>
                ))}
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setDeptRatesOpen(false)}>Cancel</Button><Button onClick={saveDeptRates} data-testid="hr-ot-rates-save" className="bg-[#C05621] text-white">Save Rates</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div className="rounded-lg border border-[#EAE5D9] overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#FAF6EE] text-[#8B7355] text-xs uppercase"><tr><th className="text-left px-3 py-2">Employee</th><th className="text-left px-3 py-2">Date</th><th className="text-right px-3 py-2">Hours</th><th className="text-right px-3 py-2">Rate ×</th><th className="text-right px-3 py-2">Amount</th><th className="text-left px-3 py-2">Reason</th><th className="text-left px-3 py-2">By</th><th></th></tr></thead>
          <tbody data-testid="hr-ot-rows">
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#EAE5D9]">
                <td className="px-3 py-2">{r.employee_name} <span className="text-xs text-[#8B7355] font-mono">· {r.employee_code}</span></td>
                <td className="px-3 py-2 text-xs">{fmtDate(r.date)}</td>
                <td className="px-3 py-2 text-right">{r.hours}</td>
                <td className="px-3 py-2 text-right">{r.rate_multiplier}×</td>
                <td className="px-3 py-2 text-right font-semibold">{inr(r.amount)}</td>
                <td className="px-3 py-2 text-xs">{r.reason || "—"}</td>
                <td className="px-3 py-2 text-xs text-[#8B7355]">{r.marked_by_name || "—"}</td>
                <td className="px-3 py-2 text-right"><button onClick={() => del(r.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-[#8B7355]">No OT records.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============================================================ */
/* PRODUCTION LINES                                             */
/* ============================================================ */
export function ProductionLinesPanel({ employees, branches, reload }) {
  const [lines, setLines] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ production_line: "", branch_id: "", employee_ids: [] });
  const load = () => api.get("/hr/production-lines").then((r) => setLines(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const toggleEmp = (id) => setF((p) => ({ ...p, employee_ids: p.employee_ids.includes(id) ? p.employee_ids.filter((x) => x !== id) : [...p.employee_ids, id] }));
  const save = async () => {
    if (!f.production_line || f.employee_ids.length === 0) return toast.error("Line name & at least one employee required");
    try { await api.post("/hr/production-lines/assign", { ...f, branch_id: f.branch_id || null }); toast.success(`Assigned ${f.employee_ids.length} to ${f.production_line}`); setOpen(false); setF({ production_line: "", branch_id: "", employee_ids: [] }); load(); reload?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const factories = branches.filter((b) => b.type === "factory");
  return (
    <div className="pt-4 space-y-3" data-testid="hr-prod-lines-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-[Outfit] text-lg font-semibold">Production Line Assignment</h3>
          <p className="text-xs text-[#8B7355]">Group employees into named production lines (Line A · Cake, Line B · Packing…). One-click multi-assign.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="hr-line-add" className="bg-[#C05621] text-white"><Factory className="w-4 h-4 mr-1" /> Assign Line</Button></DialogTrigger>
          <DialogContent className="max-w-2xl bg-white">
            <DialogHeader><DialogTitle>Assign Employees to Production Line</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Line Name *</Label><Input data-testid="hr-line-name" value={f.production_line} onChange={(e) => setF({ ...f, production_line: e.target.value })} placeholder="e.g. Line A · Cake" /></div>
                <div><Label>Factory (optional)</Label>
                  <Select value={f.branch_id || "__none__"} onValueChange={(v) => setF({ ...f, branch_id: v === "__none__" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="Keep existing branch" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-72">
                      <SelectItem value="__none__">Keep existing branch</SelectItem>
                      {factories.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Employees ({f.employee_ids.length} selected)</Label>
                <div className="border border-[#EAE5D9] rounded-md max-h-72 overflow-y-auto divide-y divide-[#EAE5D9]" data-testid="hr-line-emp-list">
                  {employees.map((e) => (
                    <label key={e.id} className="flex items-center gap-3 px-3 py-2 hover:bg-[#FAF6EE] cursor-pointer">
                      <input type="checkbox" checked={f.employee_ids.includes(e.id)} onChange={() => toggleEmp(e.id)} className="w-4 h-4" />
                      <span className="text-sm">{e.name} <span className="text-xs text-[#8B7355] font-mono">· {e.employee_code}</span></span>
                      {e.production_line && <Badge className="ml-auto bg-blue-100 text-blue-800 border-0 text-[10px]">→ {e.production_line}</Badge>}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="hr-line-save" className="bg-[#2C1810] text-white">Assign</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {lines.map((l, k) => {
          const b = branches.find((x) => x.id === l.branch_id);
          return (
            <div key={k} className="bg-white border border-[#EAE5D9] rounded-lg p-3">
              <div className="flex items-center justify-between">
                <h4 className="font-[Outfit] font-semibold text-[#2C1810]">{l.production_line}</h4>
                <Badge className="bg-orange-100 text-orange-800 border-0">{l.count} member{l.count === 1 ? "" : "s"}</Badge>
              </div>
              <div className="text-xs text-[#8B7355] mt-0.5">{b ? `${b.name} · ${b.type}` : <span className="italic">no factory set</span>}</div>
              <div className="mt-2 flex flex-wrap gap-1">
                {l.employees.slice(0, 12).map((e) => (
                  <Badge key={e.id} variant="outline" className="bg-white text-xs">{e.name}</Badge>
                ))}
                {l.employees.length > 12 && <Badge variant="outline" className="bg-white">+{l.employees.length - 12} more</Badge>}
              </div>
            </div>
          );
        })}
        {lines.length === 0 && <div className="text-center py-8 text-[#8B7355] col-span-2">No production lines yet. Click "Assign Line" to create one.</div>}
      </div>
    </div>
  );
}

/* ============================================================ */
/* UNIFORMS & ID CARD                                           */
/* ============================================================ */
export function UniformsPanel({ employees }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ employee_id: "", item: "", size: "", issued_on: new Date().toISOString().slice(0, 10), returned_on: "", condition: "new", notes: "" });
  const load = () => api.get("/hr/uniforms").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!f.employee_id || !f.item || !f.issued_on) return toast.error("Employee / item / issued-on required");
    try { await api.post("/hr/uniforms", { ...f, returned_on: f.returned_on || null }); toast.success("Issued"); setOpen(false); setF({ employee_id: "", item: "", size: "", issued_on: new Date().toISOString().slice(0, 10), returned_on: "", condition: "new", notes: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const markReturned = async (id) => { await api.patch(`/hr/uniforms/${id}`, { returned_on: new Date().toISOString().slice(0, 10) }); toast.success("Marked returned"); load(); };
  const del = async (id) => { if (!window.confirm("Delete this issue?")) return; await api.delete(`/hr/uniforms/${id}`); load(); };
  return (
    <div className="pt-4 space-y-3" data-testid="hr-uniforms-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-[Outfit] text-lg font-semibold">Uniform & ID Card Issue Register</h3>
          <p className="text-xs text-[#8B7355]">Chef coats, caps, safety shoes, ID cards — track item, size, issued-on, returned-on, condition.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="hr-uniform-add" className="bg-[#C05621] text-white"><Shirt className="w-4 h-4 mr-1" /> Issue Item</Button></DialogTrigger>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader><DialogTitle>Issue Uniform / ID Card</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Employee *</Label>
                <Select value={f.employee_id} onValueChange={(v) => setF({ ...f, employee_id: v })}>
                  <SelectTrigger data-testid="hr-uniform-emp"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-72">{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} · {e.employee_code}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Item *</Label><Input data-testid="hr-uniform-item" value={f.item} onChange={(e) => setF({ ...f, item: e.target.value })} placeholder="Chef Coat / Cap / ID Card / Shoes" /></div>
                <div><Label>Size</Label><Input value={f.size} onChange={(e) => setF({ ...f, size: e.target.value })} placeholder="M / L / 42" /></div>
                <div><Label>Issued On</Label><Input type="date" value={f.issued_on} onChange={(e) => setF({ ...f, issued_on: e.target.value })} /></div>
                <div><Label>Returned On</Label><Input type="date" value={f.returned_on} onChange={(e) => setF({ ...f, returned_on: e.target.value })} /></div>
                <div className="col-span-2"><Label>Condition</Label>
                  <Select value={f.condition} onValueChange={(v) => setF({ ...f, condition: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="new">new</SelectItem><SelectItem value="good">good</SelectItem>
                      <SelectItem value="fair">fair</SelectItem><SelectItem value="worn">worn</SelectItem>
                      <SelectItem value="lost">lost</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
              </div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="hr-uniform-save" onClick={save} className="bg-[#2C1810] text-white">Issue</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="rounded-lg border border-[#EAE5D9] overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#FAF6EE] text-[#8B7355] text-xs uppercase"><tr><th className="text-left px-3 py-2">Employee</th><th className="text-left px-3 py-2">Item</th><th className="text-left px-3 py-2">Size</th><th className="text-left px-3 py-2">Issued</th><th className="text-left px-3 py-2">Returned</th><th className="text-left px-3 py-2">Condition</th><th></th></tr></thead>
          <tbody data-testid="hr-uniform-rows">
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#EAE5D9]">
                <td className="px-3 py-2">{r.employee_name} <span className="text-xs text-[#8B7355] font-mono">· {r.employee_code}</span></td>
                <td className="px-3 py-2">{r.item}</td>
                <td className="px-3 py-2 text-xs">{r.size || "—"}</td>
                <td className="px-3 py-2 text-xs">{fmtDate(r.issued_on)}</td>
                <td className="px-3 py-2 text-xs">{r.returned_on ? fmtDate(r.returned_on) : <span className="text-[#8B7355] italic">holding</span>}</td>
                <td className="px-3 py-2"><Badge className={`border-0 ${r.condition === "new" ? "bg-blue-100 text-blue-800" : r.condition === "lost" ? "bg-red-100 text-red-800" : "bg-stone-100 text-stone-700"}`}>{r.condition}</Badge></td>
                <td className="px-3 py-2 text-right space-x-2">
                  {!r.returned_on && <Button size="sm" variant="outline" onClick={() => markReturned(r.id)}>Mark returned</Button>}
                  <button onClick={() => del(r.id)} className="text-red-500"><Trash2 className="w-4 h-4 inline" /></button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-[#8B7355]">No items issued.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============================================================ */
/* LOANS & ADVANCES                                             */
/* ============================================================ */
export function LoansPanel({ employees }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ employee_id: "", type: "advance", amount: 0, emi_months: 1, disbursed_on: new Date().toISOString().slice(0, 10), reason: "" });
  const load = () => api.get("/hr/loans").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!f.employee_id || !f.amount) return toast.error("Employee & amount required");
    try {
      const months = f.type === "advance" ? 1 : Math.max(1, Number(f.emi_months));
      await api.post("/hr/loans", { ...f, amount: Number(f.amount), emi_months: months });
      toast.success("Loan/Advance created"); setOpen(false);
      setF({ employee_id: "", type: "advance", amount: 0, emi_months: 1, disbursed_on: new Date().toISOString().slice(0, 10), reason: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const deduct = async (id) => { try { const r = await api.post(`/hr/loans/${id}/deduct`); toast.success(`Deducted ₹${r.data.deduction_amount}, bal ₹${r.data.new_balance}`); load(); } catch (e) { toast.error("Failed"); } };
  const del = async (id) => { if (!window.confirm("Delete this loan?")) return; await api.delete(`/hr/loans/${id}`); load(); };
  const active = rows.filter((r) => r.status === "active");
  const activeAmt = active.reduce((s, r) => s + Number(r.balance || 0), 0);
  return (
    <div className="pt-4 space-y-3" data-testid="hr-loans-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-[Outfit] text-lg font-semibold">Employee Loans & Advances</h3>
          <p className="text-xs text-[#8B7355]">Simple 1-shot advances OR multi-month EMI-based loans. Each "Deduct EMI" click reduces balance & is recorded.</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge className="bg-red-100 text-red-800 border-0">Outstanding ₹ {inr(activeAmt)}</Badge>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="hr-loan-add" className="bg-[#C05621] text-white"><BadgeIndianRupee className="w-4 h-4 mr-1" /> New Loan / Advance</Button></DialogTrigger>
            <DialogContent className="max-w-md bg-white">
              <DialogHeader><DialogTitle>New Loan / Advance</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Employee *</Label>
                  <Select value={f.employee_id} onValueChange={(v) => setF({ ...f, employee_id: v })}>
                    <SelectTrigger data-testid="hr-loan-emp"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-72">{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} · {e.employee_code}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Type</Label>
                    <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v, emi_months: v === "advance" ? 1 : 6 })}>
                      <SelectTrigger data-testid="hr-loan-type"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white"><SelectItem value="advance">Advance (1-shot)</SelectItem><SelectItem value="loan">Loan (EMI)</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div><Label>Amount (₹) *</Label><Input type="number" data-testid="hr-loan-amount" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} /></div>
                  {f.type === "loan" && <div><Label>EMI Months</Label><Input type="number" value={f.emi_months} onChange={(e) => setF({ ...f, emi_months: e.target.value })} data-testid="hr-loan-months" /></div>}
                  <div><Label>Disbursed On</Label><Input type="date" value={f.disbursed_on} onChange={(e) => setF({ ...f, disbursed_on: e.target.value })} /></div>
                </div>
                <div><Label>Reason</Label><Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} /></div>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="hr-loan-save" onClick={save} className="bg-[#2C1810] text-white">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div className="rounded-lg border border-[#EAE5D9] overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#FAF6EE] text-[#8B7355] text-xs uppercase"><tr><th className="text-left px-3 py-2">Employee</th><th className="text-left px-3 py-2">Type</th><th className="text-right px-3 py-2">Amount</th><th className="text-right px-3 py-2">EMI × Months</th><th className="text-right px-3 py-2">Balance</th><th className="text-left px-3 py-2">Status</th><th></th></tr></thead>
          <tbody data-testid="hr-loans-rows">
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#EAE5D9]">
                <td className="px-3 py-2">{r.employee_name} <span className="text-xs text-[#8B7355] font-mono">· {r.employee_code}</span></td>
                <td className="px-3 py-2"><Badge className={`border-0 ${r.type === "advance" ? "bg-amber-100 text-amber-800" : "bg-purple-100 text-purple-800"}`}>{r.type}</Badge></td>
                <td className="px-3 py-2 text-right font-semibold">{inr(r.amount)}</td>
                <td className="px-3 py-2 text-right text-xs">{inr(r.emi_amount)} × {r.emi_months} <span className="text-[#8B7355]">({r.months_deducted || 0} done)</span></td>
                <td className="px-3 py-2 text-right font-semibold text-red-700">{inr(r.balance)}</td>
                <td className="px-3 py-2"><Badge className={`border-0 ${r.status === "closed" ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800"}`}>{r.status}</Badge></td>
                <td className="px-3 py-2 text-right space-x-2">
                  {r.status === "active" && <Button size="sm" variant="outline" onClick={() => deduct(r.id)} data-testid={`hr-loan-deduct-${r.id}`}>Deduct EMI</Button>}
                  <button onClick={() => del(r.id)} className="text-red-500"><Trash2 className="w-4 h-4 inline" /></button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-[#8B7355]">No loans or advances yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============================================================ */
/* DOCUMENTS                                                    */
/* ============================================================ */
export function DocumentsPanel({ employees }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ employee_id: "", doc_type: "aadhaar", doc_number: "", issued_on: "", expiry_date: "", notes: "" });
  const load = () => api.get("/hr/documents").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!f.employee_id || !f.doc_type) return toast.error("Employee & doc type required");
    try { await api.post("/hr/documents", f); toast.success("Saved"); setOpen(false); setF({ employee_id: "", doc_type: "aadhaar", doc_number: "", issued_on: "", expiry_date: "", notes: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete this document?")) return; await api.delete(`/hr/documents/${id}`); load(); };
  const today = new Date().toISOString().slice(0, 10);
  const in30 = new Date(Date.now() + 30 * 86400e3).toISOString().slice(0, 10);
  return (
    <div className="pt-4 space-y-3" data-testid="hr-documents-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-[Outfit] text-lg font-semibold">Employee Documents</h3>
          <p className="text-xs text-[#8B7355]">Aadhaar / PAN / Driving Licence / Medical / Food Handler certificates — tracked with expiry.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="hr-doc-add" className="bg-[#C05621] text-white"><FileWarning className="w-4 h-4 mr-1" /> Add Document</Button></DialogTrigger>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader><DialogTitle>Add Employee Document</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Employee *</Label>
                <Select value={f.employee_id} onValueChange={(v) => setF({ ...f, employee_id: v })}>
                  <SelectTrigger data-testid="hr-doc-emp"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-72">{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} · {e.employee_code}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Type</Label>
                  <Select value={f.doc_type} onValueChange={(v) => setF({ ...f, doc_type: v })}>
                    <SelectTrigger data-testid="hr-doc-type"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="aadhaar">Aadhaar</SelectItem>
                      <SelectItem value="pan">PAN</SelectItem>
                      <SelectItem value="driving_licence">Driving Licence</SelectItem>
                      <SelectItem value="medical_cert">Medical Certificate</SelectItem>
                      <SelectItem value="food_handler">Food Handler Cert</SelectItem>
                      <SelectItem value="passport">Passport</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Number</Label><Input value={f.doc_number} onChange={(e) => setF({ ...f, doc_number: e.target.value })} /></div>
                <div><Label>Issued On</Label><Input type="date" value={f.issued_on} onChange={(e) => setF({ ...f, issued_on: e.target.value })} /></div>
                <div><Label>Expiry Date</Label><Input type="date" value={f.expiry_date} onChange={(e) => setF({ ...f, expiry_date: e.target.value })} data-testid="hr-doc-expiry" /></div>
                <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
              </div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="hr-doc-save" onClick={save} className="bg-[#2C1810] text-white">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="rounded-lg border border-[#EAE5D9] overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#FAF6EE] text-[#8B7355] text-xs uppercase"><tr><th className="text-left px-3 py-2">Employee</th><th className="text-left px-3 py-2">Type</th><th className="text-left px-3 py-2">Number</th><th className="text-left px-3 py-2">Issued</th><th className="text-left px-3 py-2">Expiry</th><th></th></tr></thead>
          <tbody data-testid="hr-doc-rows">
            {rows.map((r) => {
              const expTone = !r.expiry_date ? "slate" : r.expiry_date < today ? "red" : r.expiry_date <= in30 ? "amber" : "green";
              return (
                <tr key={r.id} className="border-t border-[#EAE5D9]">
                  <td className="px-3 py-2">{r.employee_name} <span className="text-xs text-[#8B7355] font-mono">· {r.employee_code}</span></td>
                  <td className="px-3 py-2 capitalize">{(r.doc_type || "").replace(/_/g, " ")}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.doc_number || "—"}</td>
                  <td className="px-3 py-2 text-xs">{r.issued_on ? fmtDate(r.issued_on) : "—"}</td>
                  <td className="px-3 py-2">
                    {r.expiry_date ? (
                      <Badge className={`border-0 ${expTone === "red" ? "bg-red-100 text-red-800" : expTone === "amber" ? "bg-amber-100 text-amber-800" : expTone === "green" ? "bg-emerald-100 text-emerald-800" : "bg-stone-100 text-stone-700"}`}>{fmtDate(r.expiry_date)}</Badge>
                    ) : <span className="text-[#8B7355] italic text-xs">no expiry</span>}
                  </td>
                  <td className="px-3 py-2 text-right"><button onClick={() => del(r.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-[#8B7355]">No documents yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============================================================ */
/* REPORTS                                                      */
/* ============================================================ */
export function ReportsPanel({ employees }) {
  const [sub, setSub] = useState("attendance");
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = new Date(); monthStart.setDate(1);
  const [range, setRange] = useState({ from: monthStart.toISOString().slice(0, 10), to: today });
  const [month, setMonth] = useState({ from: today.slice(0, 7), to: today.slice(0, 7) });
  const [data, setData] = useState(null);

  const run = async () => {
    try {
      if (sub === "attendance") setData((await api.get("/hr/reports/attendance", { params: range })).data);
      else if (sub === "leave") setData((await api.get("/hr/reports/leaves", { params: range })).data);
      else if (sub === "salary") setData((await api.get("/hr/reports/salary", { params: { month_from: month.from, month_to: month.to } })).data);
      else if (sub === "overtime") setData((await api.get("/hr/reports/overtime", { params: range })).data);
      else if (sub === "dept") setData((await api.get("/hr/reports/department-wise")).data);
      else if (sub === "joinres") setData((await api.get("/hr/reports/joiners-resignations", { params: { from_date: range.from, to_date: range.to } })).data);
      else if (sub === "emplist") setData({ employees });
    } catch (e) { toast.error("Report failed"); }
  };
  useEffect(() => { run(); /* eslint-disable-next-line */ }, [sub]);

  const download = (rows, filename) => {
    if (!rows || !rows.length) { toast.error("Nothing to export"); return; }
    const headers = Object.keys(rows[0]);
    const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => JSON.stringify(r[h] ?? "")).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const subs = [
    { id: "attendance", label: "Attendance", icon: CalendarDays },
    { id: "leave", label: "Leaves", icon: ClipboardList },
    { id: "salary", label: "Salary", icon: IndianRupee },
    { id: "overtime", label: "Overtime", icon: Timer },
    { id: "emplist", label: "Employee List", icon: Users },
    { id: "dept", label: "Department-wise", icon: TrendingUp },
    { id: "joinres", label: "Joiners & Resignations", icon: UserPlus },
  ];

  return (
    <div className="pt-4 space-y-3" data-testid="hr-reports-panel">
      <div className="flex flex-wrap gap-1.5">
        {subs.map((s) => (
          <button key={s.id} onClick={() => setSub(s.id)} data-testid={`hr-rep-${s.id}`}
            className={cls("border rounded-md px-3 py-1.5 text-xs font-medium flex items-center gap-1.5",
              sub === s.id ? "bg-[#C05621] text-white border-[#9C4221]" : "bg-white text-[#5C4033] border-[#EAE5D9] hover:bg-[#FAF6EE]")}>
            <s.icon className="w-3.5 h-3.5" /> {s.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-[#EAE5D9] rounded-lg p-4">
        {(sub === "attendance" || sub === "leave" || sub === "overtime" || sub === "joinres") && (
          <div className="flex flex-wrap gap-2 items-end mb-3">
            <div><Label>From</Label><Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} /></div>
            <div><Label>To</Label><Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} /></div>
            <Button onClick={run} className="bg-[#C05621] text-white">Run Report</Button>
          </div>
        )}
        {sub === "salary" && (
          <div className="flex flex-wrap gap-2 items-end mb-3">
            <div><Label>From Month</Label><Input type="month" value={month.from} onChange={(e) => setMonth({ ...month, from: e.target.value })} /></div>
            <div><Label>To Month</Label><Input type="month" value={month.to} onChange={(e) => setMonth({ ...month, to: e.target.value })} /></div>
            <Button onClick={run} className="bg-[#C05621] text-white">Run Report</Button>
          </div>
        )}
        {data && sub === "attendance" && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-3">
              {Object.entries(data.by_status || {}).map(([k, v]) => <Badge key={k} className="bg-blue-100 text-blue-800 border-0 capitalize">{k.replace("_", " ")}: {v}</Badge>)}
              <Button size="sm" variant="outline" onClick={() => download(data.rows, "attendance.csv")}><Download className="w-3 h-3 mr-1" /> CSV</Button>
            </div>
            <SimpleTable rows={data.by_employee} cols={["employee_name", "present", "absent", "half_day", "leave"]} />
          </div>
        )}
        {data && sub === "leave" && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-3">
              <Badge className="bg-purple-100 text-purple-800 border-0">Total days: {data.total_days}</Badge>
              {Object.entries(data.by_type || {}).map(([k, v]) => <Badge key={k} className="bg-blue-100 text-blue-800 border-0 capitalize">{k}: {v}</Badge>)}
              {Object.entries(data.by_status || {}).map(([k, v]) => <Badge key={k} className="bg-amber-100 text-amber-800 border-0 capitalize">{k}: {v}</Badge>)}
              <Button size="sm" variant="outline" onClick={() => download(data.rows, "leaves.csv")}><Download className="w-3 h-3 mr-1" /> CSV</Button>
            </div>
            <SimpleTable rows={data.rows} cols={["employee_name", "leave_type", "from_date", "to_date", "days", "status"]} />
          </div>
        )}
        {data && sub === "salary" && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-3">
              <Badge className="bg-purple-100 text-purple-800 border-0">Total: ₹{inr(data.total)}</Badge>
              <Badge className="bg-emerald-100 text-emerald-800 border-0">Paid: ₹{inr(data.paid)}</Badge>
              <Badge className="bg-red-100 text-red-800 border-0">Unpaid: ₹{inr(data.unpaid)}</Badge>
              <Button size="sm" variant="outline" onClick={() => download(data.rows, "salary.csv")}><Download className="w-3 h-3 mr-1" /> CSV</Button>
            </div>
            <SimpleTable rows={data.by_month} cols={["month", "count", "total", "paid"]} />
          </div>
        )}
        {data && sub === "overtime" && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-3">
              <Badge className="bg-purple-100 text-purple-800 border-0">Total hours: {data.total_hours}</Badge>
              <Badge className="bg-purple-100 text-purple-800 border-0">Total ₹{inr(data.total_amount)}</Badge>
              <Button size="sm" variant="outline" onClick={() => download(data.rows, "overtime.csv")}><Download className="w-3 h-3 mr-1" /> CSV</Button>
            </div>
            <SimpleTable rows={data.by_employee} cols={["employee_name", "hours", "amount"]} />
          </div>
        )}
        {data && sub === "dept" && (
          <div className="space-y-3">
            <Button size="sm" variant="outline" onClick={() => download(data, "department-wise.csv")}><Download className="w-3 h-3 mr-1" /> CSV</Button>
            <SimpleTable rows={data} cols={["department", "total", "active"]} />
          </div>
        )}
        {data && sub === "emplist" && (
          <div className="space-y-3">
            <Button size="sm" variant="outline" onClick={() => download(data.employees, "employee-list.csv")}><Download className="w-3 h-3 mr-1" /> CSV</Button>
            <SimpleTable rows={data.employees} cols={["name", "employee_code", "department", "designation", "phone", "salary", "join_date", "active"]} />
          </div>
        )}
        {data && sub === "joinres" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1"><h4 className="font-semibold">Joiners ({data.joiners?.length || 0})</h4>
                <Button size="sm" variant="outline" onClick={() => download(data.joiners, "joiners.csv")}><Download className="w-3 h-3 mr-1" /> CSV</Button>
              </div>
              <SimpleTable rows={data.joiners} cols={["name", "employee_code", "department", "join_date"]} />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1"><h4 className="font-semibold">Resignations ({data.resignations?.length || 0})</h4>
                <Button size="sm" variant="outline" onClick={() => download(data.resignations, "resignations.csv")}><Download className="w-3 h-3 mr-1" /> CSV</Button>
              </div>
              <SimpleTable rows={data.resignations} cols={["employee_name", "employee_code", "department", "resignation_date", "reason"]} />
            </div>
            <div className="col-span-2 text-sm text-[#8B7355]">Net change: <b className={data.net_change >= 0 ? "text-emerald-700" : "text-red-700"}>{data.net_change >= 0 ? "+" : ""}{data.net_change}</b></div>
          </div>
        )}
      </div>
    </div>
  );
}

function SimpleTable({ rows, cols }) {
  if (!rows || rows.length === 0) return <div className="text-[#8B7355] text-sm py-6 text-center">No data.</div>;
  return (
    <div className="rounded-lg border border-[#EAE5D9] overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-[#FAF6EE] text-[#8B7355] text-xs uppercase"><tr>{cols.map((c) => <th key={c} className="text-left px-3 py-2">{c.replace(/_/g, " ")}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, k) => (
            <tr key={r.id || k} className="border-t border-[#EAE5D9]">
              {cols.map((c) => {
                let v = r[c];
                if (typeof v === "boolean") v = v ? "yes" : "no";
                if ((c.includes("date") || c === "month") && v) v = fmtDate(v);
                if ((c === "amount" || c === "total" || c === "paid" || c === "unpaid" || c === "salary") && typeof v === "number") v = "₹" + inr(v);
                return <td key={c} className="px-3 py-2">{v ?? "—"}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ============================================================ */
/* RESIGNATION quick-action (used by Employee row) — exported   */
/* ============================================================ */
export function ResignButton({ employee, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ resignation_date: new Date().toISOString().slice(0, 10), last_working_date: "", reason: "" });
  const save = async () => {
    try { await api.post("/hr/resignations", { ...f, employee_id: employee.id }); toast.success("Resignation recorded"); setOpen(false); onDone?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <>
      <button data-testid={`hr-resign-${employee.id}`} onClick={(e) => { e.stopPropagation(); setOpen(true); }} className="text-amber-600 hover:text-amber-800" title="Record Resignation"><UserMinus className="w-4 h-4" /></button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle>Record Resignation — {employee.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Resignation Date</Label><Input type="date" value={f.resignation_date} onChange={(e) => setF({ ...f, resignation_date: e.target.value })} /></div>
            <div><Label>Last Working Date</Label><Input type="date" value={f.last_working_date} onChange={(e) => setF({ ...f, last_working_date: e.target.value })} /></div>
            <div><Label>Reason</Label><Textarea rows={2} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="hr-resign-save" className="bg-[#2C1810] text-white">Record</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
